// Which parts of a form definition staff fill (no React here).
//
// A part is staff-only when its page OR its section OR the item itself is
// `audience: "staff"` (the patient wizard never shows it). The server's
// staff_fields(defn) is the exact complement of routes_public.patient_fields
// over field items; staffKeys() here follows the same rule (parity fixture:
// __fixtures__/staff_keys.json, generated by formwiz/parity/gen_staff_keys.mjs).
import { isField, optionsFor } from "../engine/model";

const isStaff = (node) => !!node && node.audience === "staff";

function* walk(def) {
  for (const page of (def && def.pages) || []) {
    for (const section of page.sections || []) {
      for (const item of section.items || []) yield { page, section, item };
    }
  }
}

export function effectiveStaff(page, section, item) {
  return isStaff(page) || isStaff(section) || isStaff(item);
}

// Field ids staff answer (every staff-only field item with an id).
export function staffKeys(def) {
  const out = [];
  for (const { page, section, item } of walk(def)) {
    if (item.id && isField(item) && effectiveStaff(page, section, item)) out.push(item.id);
  }
  return out;
}

// Field ids the patient answers.
export function patientFieldIds(def) {
  const out = [];
  for (const { page, section, item } of walk(def)) {
    if (item.id && isField(item) && !effectiveStaff(page, section, item)) out.push(item.id);
  }
  return out;
}

export function hasStaffFields(def) {
  return staffKeys(def).length > 0;
}

// An internal form: staff fill it for a patient and it is never sent (no
// field the patient could answer; the server's patient_fillable == false).
export function isInternalDef(def) {
  if (!def || !Array.isArray(def.pages)) return false;
  return patientFieldIds(def).length === 0 && staffKeys(def).length > 0;
}

// The signers the patient (or a guardian) is for: they sign on the staff
// device, so such a form opens in the patient's forms language.
export function patientSigners(def) {
  const out = [];
  for (const { item } of walk(def)) {
    if (item.type === "signature" && (item.signer === "patient" || item.signer === "guardian") && !out.includes(item.signer)) out.push(item.signer);
  }
  return out;
}

// Every key that belongs to a staff field: the ids and their "__other" text.
export function staffAnswerKeys(def) {
  const ids = new Set(staffKeys(def));
  const out = new Set(ids);
  for (const { item } of walk(def)) {
    if (ids.has(item.id) && optionsFor(item).some((o) => o.other)) out.add(`${item.id}__other`);
  }
  return out;
}

// The patient's part of a submission's answers: every staff key dropped, so
// a staff answer someone cleared does not come back from the old answers.
export function patientPart(def, answers) {
  const drop = staffAnswerKeys(def);
  const out = {};
  Object.entries(answers || {}).forEach(([k, v]) => {
    const baseId = k.endsWith("__other") ? k.slice(0, -7) : k;
    if (!drop.has(k) && !drop.has(baseId)) out[k] = v;
  });
  return out;
}

// The staff part of a submission's answers (ids and "__other" keys).
export function staffPart(def, answers) {
  const keep = staffAnswerKeys(def);
  const out = {};
  Object.entries(answers || {}).forEach(([k, v]) => {
    if (keep.has(k)) out[k] = v;
  });
  return out;
}

const hasStaffTableColumns = (item) => item.type === "table" && (item.tableColumns || []).some((c) => c.audience === "staff");

/*
 * The staff-only parts of a definition, for the staff-sections screen: pages
 * and sections that are staff-only whole, plus staff-only items inside a
 * patient section. Patient tables with staff columns are left out (phase 2:
 * table staff columns are not editable in staff sections v1). Scoring is
 * dropped (it refers to patient answers; the server recomputes scores from
 * the merged answers). Conditions on patient answers still evaluate: the
 * screen loads the whole submission's answers.
 */
export function staffOnlyDefinition(def) {
  if (!def) return def;
  const pages = [];
  for (const page of def.pages || []) {
    if (isStaff(page)) {
      pages.push(page);
      continue;
    }
    const sections = [];
    for (const section of page.sections || []) {
      if (isStaff(section)) {
        sections.push(section);
        continue;
      }
      const items = (section.items || []).filter((it) => isStaff(it) && !hasStaffTableColumns(it));
      if (items.some(isField)) sections.push({ ...section, audience: "staff", items });
    }
    if (sections.length) pages.push({ ...page, audience: "staff", sections });
  }
  const { scoring, ...rest } = def; // eslint-disable-line no-unused-vars
  return { ...rest, pages };
}
